import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, wireApi, payeeApi, wireTemplateApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Printer, Trash2, Banknote, Pencil, Send, Clock, Mail, BookmarkPlus, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { WirePrintView } from '../components/WirePrintView';

const initialFormData = {
  llc_id: '',
  payee_id: '',
  beneficiary_name: '',
  beneficiary_address: '',
  beneficiary_city_state_zip: '',
  beneficiary_email: '',
  beneficiary_bank_name: '',
  beneficiary_bank_routing: '',
  beneficiary_bank_account: '',
  amount: '',
  purpose: '',
  reference: '',
};

export const WireInstructionsPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [payees, setPayees] = useState([]);
  const [wires, setWires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [selectedWire, setSelectedWire] = useState(null);
  const [useCustomBeneficiary, setUseCustomBeneficiary] = useState(false);
  const [editingWire, setEditingWire] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Wire-Instructions-${selectedWire?.id?.slice(0, 8) || 'document'}`,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.llc_id) {
      fetchPayees(formData.llc_id);
    }
  }, [formData.llc_id]);

  const fetchData = async () => {
    try {
      const [llcsRes, wiresRes, templatesRes] = await Promise.all([
        llcApi.list(),
        wireApi.list(),
        wireTemplateApi.list(),
      ]);
      setLlcs(llcsRes.data);
      setWires(wiresRes.data);
      setTemplates(templatesRes.data);
      if (llcsRes.data.length > 0) {
        setFormData(prev => ({ ...prev, llc_id: llcsRes.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayees = async (llcId) => {
    try {
      const response = await payeeApi.list(llcId);
      setPayees(response.data);
    } catch (error) {
      console.error('Failed to fetch payees:', error);
      setPayees([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLLCChange = (llcId) => {
    setFormData(prev => ({ 
      ...prev, 
      llc_id: llcId,
      payee_id: '',
      beneficiary_name: '',
      beneficiary_address: '',
      beneficiary_city_state_zip: '',
      beneficiary_email: '',
      beneficiary_bank_name: '',
      beneficiary_bank_routing: '',
      beneficiary_bank_account: '',
    }));
    setUseCustomBeneficiary(false);
  };

  const handlePayeeChange = (payeeId) => {
    if (payeeId === '__custom__') {
      setUseCustomBeneficiary(true);
      setFormData(prev => ({
        ...prev,
        payee_id: '',
        beneficiary_name: '',
        beneficiary_address: '',
        beneficiary_city_state_zip: '',
        beneficiary_email: '',
        beneficiary_bank_name: '',
        beneficiary_bank_routing: '',
        beneficiary_bank_account: '',
      }));
    } else {
      const payee = payees.find(p => p.id === payeeId);
      if (payee) {
        const cityStateZip = [payee.city, payee.state, payee.zip_code].filter(Boolean).join(', ');
        setFormData(prev => ({
          ...prev,
          payee_id: payeeId,
          beneficiary_name: payee.name,
          beneficiary_address: payee.address_line1 + (payee.address_line2 ? `, ${payee.address_line2}` : ''),
          beneficiary_city_state_zip: cityStateZip,
          beneficiary_email: payee.email || '',
          beneficiary_bank_name: payee.bank_name || '',
          beneficiary_bank_routing: payee.bank_routing || '',
          beneficiary_bank_account: payee.bank_account || '',
        }));
        setUseCustomBeneficiary(false);
      }
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, amount: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const wireData = {
        llc_id: formData.llc_id,
        beneficiary_name: formData.beneficiary_name,
        beneficiary_address: formData.beneficiary_address,
        beneficiary_city_state_zip: formData.beneficiary_city_state_zip,
        beneficiary_email: formData.beneficiary_email,
        beneficiary_bank_name: formData.beneficiary_bank_name,
        beneficiary_bank_routing: formData.beneficiary_bank_routing,
        beneficiary_bank_account: formData.beneficiary_bank_account,
        amount: parseFloat(formData.amount),
        purpose: formData.purpose,
        reference: formData.reference,
      };

      let response;
      if (editingWire) {
        response = await wireApi.update(editingWire.id, wireData);
        toast.success('Wire instruction updated');
      } else {
        response = await wireApi.create(wireData);
        toast.success('Wire instruction created');
      }
      
      setDialogOpen(false);
      setFormData({ ...initialFormData, llc_id: formData.llc_id });
      setUseCustomBeneficiary(false);
      setEditingWire(null);
      setSelectedWire(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingWire ? 'update' : 'create'} wire instruction`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (wire) => {
    setEditingWire(wire);
    setFormData({
      llc_id: wire.llc_id,
      payee_id: '',
      beneficiary_name: wire.beneficiary_name,
      beneficiary_address: wire.beneficiary_address,
      beneficiary_city_state_zip: wire.beneficiary_city_state_zip,
      beneficiary_email: wire.beneficiary_email || '',
      beneficiary_bank_name: wire.beneficiary_bank_name || '',
      beneficiary_bank_routing: wire.beneficiary_bank_routing || '',
      beneficiary_bank_account: wire.beneficiary_bank_account || '',
      amount: wire.amount.toString(),
      purpose: wire.purpose || '',
      reference: wire.reference || '',
    });
    setUseCustomBeneficiary(true);
    setDialogOpen(true);
  };

  const handleDelete = async (wireId) => {
    try {
      await wireApi.delete(wireId);
      toast.success('Wire instruction deleted');
      if (selectedWire?.id === wireId) {
        setSelectedWire(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete wire instruction');
    }
  };

  const handleStatusToggle = async (wire) => {
    const newStatus = wire.status === 'sent' ? 'pending' : 'sent';
    try {
      await wireApi.updateStatus(wire.id, newStatus);
      toast.success(`Wire marked as ${newStatus}`);
      fetchData();
      if (selectedWire?.id === wire.id) {
        setSelectedWire({ ...selectedWire, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update wire status');
    }
  };

  const handleEmailWire = async () => {
    if (!selectedWire || !emailAddress.trim()) return;
    setEmailSending(true);
    try {
      await wireApi.email(selectedWire.id, emailAddress.trim());
      toast.success(`Wire instructions sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const openEmailDialog = () => {
    setEmailAddress(selectedWire?.beneficiary_email || '');
    setEmailDialogOpen(true);
  };

  const handleSaveAsTemplate = async () => {
    if (!selectedWire || !templateName.trim()) return;
    try {
      await wireTemplateApi.create({
        name: templateName.trim(),
        beneficiary_name: selectedWire.beneficiary_name,
        beneficiary_address: selectedWire.beneficiary_address,
        beneficiary_city_state_zip: selectedWire.beneficiary_city_state_zip,
        beneficiary_email: selectedWire.beneficiary_email || '',
        beneficiary_bank_name: selectedWire.beneficiary_bank_name || '',
        beneficiary_bank_routing: selectedWire.beneficiary_bank_routing || '',
        beneficiary_bank_account: selectedWire.beneficiary_bank_account || '',
        purpose: selectedWire.purpose || '',
      });
      toast.success(`Template "${templateName}" saved`);
      setSaveTemplateDialogOpen(false);
      setTemplateName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleApplyTemplate = (templateId) => {
    if (templateId === '__none__') return;
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        payee_id: '',
        beneficiary_name: template.beneficiary_name,
        beneficiary_address: template.beneficiary_address,
        beneficiary_city_state_zip: template.beneficiary_city_state_zip,
        beneficiary_email: template.beneficiary_email || '',
        beneficiary_bank_name: template.beneficiary_bank_name || '',
        beneficiary_bank_routing: template.beneficiary_bank_routing || '',
        beneficiary_bank_account: template.beneficiary_bank_account || '',
        purpose: template.purpose || '',
      }));
      setUseCustomBeneficiary(true);
      toast.success(`Template "${template.name}" applied`);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await wireTemplateApi.delete(templateId);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getLLCName = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    return llc?.company_name || 'Unknown';
  };

  const getLLC = (llcId) => {
    return llcs.find(l => l.id === llcId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="wire-instructions-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Wire Instructions</h1>
          <p className="text-slate-500 mt-1">Create and print domestic wire transfer instructions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingWire(null);
            setFormData({ ...initialFormData, llc_id: llcs[0]?.id || '' });
            setUseCustomBeneficiary(false);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={llcs.length === 0} data-testid="create-wire-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Wire
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingWire ? 'Edit Wire Instructions' : 'Create Wire Instructions'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="llc_id">Sending LLC</Label>
                <Select value={formData.llc_id} onValueChange={handleLLCChange}>
                  <SelectTrigger data-testid="wire-llc-select">
                    <SelectValue placeholder="Select LLC" />
                  </SelectTrigger>
                  <SelectContent>
                    {llcs.map(llc => (
                      <SelectItem key={llc.id} value={llc.id}>
                        {llc.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Beneficiary</Label>
                {!useCustomBeneficiary ? (
                  <Select value={formData.payee_id || '__custom__'} onValueChange={handlePayeeChange}>
                    <SelectTrigger data-testid="wire-payee-select">
                      <SelectValue placeholder="Select a payee" />
                    </SelectTrigger>
                    <SelectContent>
                      {payees.map(payee => (
                        <SelectItem key={payee.id} value={payee.id}>
                          {payee.name}
                          {payee.bank_name && <span className="text-slate-400 ml-2">({payee.bank_name})</span>}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__" className="text-slate-500 border-t mt-1 pt-1">
                        + Enter custom beneficiary...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Button 
                    type="button" 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-xs"
                    onClick={() => setUseCustomBeneficiary(false)}
                  >
                    ← Back to payee list
                  </Button>
                )}
              </div>

              {/* Wire Templates */}
              {templates.length > 0 && (
                <div>
                  <Label>Or use a saved template</Label>
                  <Select value="__none__" onValueChange={handleApplyTemplate}>
                    <SelectTrigger data-testid="wire-template-select">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" disabled>Select a template...</SelectItem>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="flex items-center gap-2">
                            <FileText className="w-3 h-3 text-slate-400" />
                            {t.name} — {t.beneficiary_name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700">Beneficiary Details</h4>
                <div>
                  <Label htmlFor="beneficiary_name">Name</Label>
                  <Input
                    id="beneficiary_name"
                    name="beneficiary_name"
                    value={formData.beneficiary_name}
                    onChange={handleInputChange}
                    required
                    data-testid="wire-beneficiary-input"
                  />
                </div>

                <div>
                  <Label htmlFor="beneficiary_address">Address</Label>
                  <Input
                    id="beneficiary_address"
                    name="beneficiary_address"
                    value={formData.beneficiary_address}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="beneficiary_city_state_zip">City, State, ZIP</Label>
                  <Input
                    id="beneficiary_city_state_zip"
                    name="beneficiary_city_state_zip"
                    value={formData.beneficiary_city_state_zip}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="beneficiary_email">Notification Email</Label>
                  <Input
                    id="beneficiary_email"
                    name="beneficiary_email"
                    type="email"
                    value={formData.beneficiary_email}
                    onChange={handleInputChange}
                    placeholder="Email to notify of wire transfer"
                  />
                </div>
              </div>

              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700">Beneficiary Bank</h4>
                <div>
                  <Label htmlFor="beneficiary_bank_name">Bank Name</Label>
                  <Input
                    id="beneficiary_bank_name"
                    name="beneficiary_bank_name"
                    value={formData.beneficiary_bank_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="beneficiary_bank_routing">Routing Number</Label>
                    <Input
                      id="beneficiary_bank_routing"
                      name="beneficiary_bank_routing"
                      value={formData.beneficiary_bank_routing}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="beneficiary_bank_account">Account Number</Label>
                    <Input
                      id="beneficiary_bank_account"
                      name="beneficiary_bank_account"
                      value={formData.beneficiary_bank_account}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  className="font-mono"
                  required
                  data-testid="wire-amount-input"
                />
              </div>

              <div>
                <Label htmlFor="purpose">Purpose (Optional)</Label>
                <Input
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="reference">Reference (Optional)</Label>
                <Input
                  id="reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="wire-submit-btn">
                  {submitting ? (editingWire ? 'Updating...' : 'Creating...') : (editingWire ? 'Update Wire' : 'Create Wire')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Wire List */}
        <Card className="border-slate-200">
          <CardHeader className="rounded-t-lg panel-header-themed" >
            <CardTitle className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <Banknote className="w-5 h-5" />
              Wire History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {wires.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Banknote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p>No wire instructions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wires.map(wire => (
                  <div
                    key={wire.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedWire?.id === wire.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setSelectedWire(wire)}
                    data-testid={`wire-item-${wire.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{wire.beneficiary_name}</div>
                        <div className="text-sm text-slate-500">{getLLCName(wire.llc_id)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-semibold text-slate-900">{formatCurrency(wire.amount)}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(wire.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        {wire.status === 'sent' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            <Send className="w-3 h-3" />
                            Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`sent-${wire.id}`} className="text-xs text-slate-500">
                          Mark as sent
                        </Label>
                        <Switch
                          id={`sent-${wire.id}`}
                          checked={wire.status === 'sent'}
                          onCheckedChange={() => handleStatusToggle(wire)}
                          data-testid={`wire-sent-toggle-${wire.id}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Wire Preview */}
        <Card className="border-slate-200">
          <CardHeader className="rounded-t-lg flex flex-row items-center justify-between panel-header-themed" >
            <CardTitle style={{ color: '#ffffff' }}>Wire Document</CardTitle>
            {selectedWire && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleEdit(selectedWire)} data-testid="wire-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" onClick={openEmailDialog} data-testid="wire-email-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button variant="outline" onClick={() => { setTemplateName(selectedWire.beneficiary_name); setSaveTemplateDialogOpen(true); }} data-testid="wire-save-template-btn">
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                  Save Template
                </Button>
                <Button onClick={handlePrint} data-testid="wire-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Wire Instruction?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this wire instruction.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(selectedWire.id)} className="bg-red-600 hover:bg-red-700">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {selectedWire ? (
              <div className="wire-preview-wrapper overflow-hidden">
                <div className="transform scale-[0.85] origin-top-left" style={{ width: '118%' }}>
                  <WirePrintView
                    ref={printRef}
                    wire={selectedWire}
                    llc={getLLC(selectedWire.llc_id)}
                  />
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                Select a wire instruction to preview
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Wire Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md" data-testid="email-wire-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-600" />
              Email Wire Instructions
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="email-to" className="mb-2 block">Recipient email</Label>
            <Input
              id="email-to"
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="recipient@example.com"
              data-testid="email-wire-input"
              autoFocus
            />
            {selectedWire && (
              <p className="text-xs text-slate-400 mt-2">
                Sending wire for <span className="font-medium text-slate-600">{selectedWire.beneficiary_name}</span> — ${selectedWire.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEmailWire} disabled={emailSending || !emailAddress.trim()} data-testid="email-wire-send-btn">
              {emailSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-slate-600" />
              Save as Wire Template
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="template-name" className="mb-2 block">Template name</Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., CliftonLarson - Monthly Retainer"
              data-testid="template-name-input"
              autoFocus
            />
            {selectedWire && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-500 space-y-1">
                <div><span className="font-medium text-slate-700">Beneficiary:</span> {selectedWire.beneficiary_name}</div>
                <div><span className="font-medium text-slate-700">Bank:</span> {selectedWire.beneficiary_bank_name || 'N/A'}</div>
                <div><span className="font-medium text-slate-700">Account:</span> {selectedWire.beneficiary_bank_account || 'N/A'}</div>
                {selectedWire.purpose && <div><span className="font-medium text-slate-700">Purpose:</span> {selectedWire.purpose}</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAsTemplate} disabled={!templateName.trim()} data-testid="save-template-confirm-btn">
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Management */}
      {templates.length > 0 && (
        <Card className="border-slate-200 mt-6">
          <CardHeader className="rounded-t-lg panel-header-themed" >
            <CardTitle className="flex items-center gap-2 text-sm" style={{ color: '#ffffff' }}>
              <FileText className="w-4 h-4" />
              Saved Wire Templates ({templates.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-sm text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">
                      {t.beneficiary_name} &middot; {t.beneficiary_bank_name || 'No bank'} &middot; {t.beneficiary_bank_account ? `...${t.beneficiary_bank_account.slice(-4)}` : 'No acct'}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete template "{t.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently delete this wire template.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteTemplate(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
